import { useEffect, useMemo, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { useNavigate } from "react-router-dom";

import {
  Badge,
  Button,
  Gallery,
  Heading,
  Icon,
  Notification,
  Span,
  Spinner,
  TabGroup,
} from "@icg360/design-system";

import { BaseLayout } from "components/base-layout";
import { FetchingError } from "components/common/fetching-error";
import { Stack } from "components/common/stack";
import { FaqSidebar } from "components/shared/faq-sidebar";
import { InsuranceRepresentativeNew } from "components/shared/insurance-representative-new";
import { MainLayout } from "components/shared/main-layout";
import { ThumbtackModalProvider } from "components/thumbtack-modal";
import { screenSizes } from "consts";
import { usePolicyDetail } from "data/ss-data-hooks";
import {
  downloadFile,
  downloadImage,
  formatDate,
  hideSupportChat,
  showSupportChat,
  useFlags,
} from "utils";

import { useInspectionsData } from "./hooks";
import galleryMediaTileStyles from "./inspection-gallery-media-tiles.module.scss";
import { type MediaTileItem } from "./inspection-gallery-types";
import { sortInspectionAssetIdsByLastUpdatedDesc } from "./inspections-helpers";
import { InspectionsIneligible } from "./inspections-ineligible";
import { InspectionsIneligibleOld } from "./inspections-ineligible-old";
import styles from "./inspections.module.scss";
import { formatReferralWriteOutCardSubheading } from "./referral-display";
import { RoofMarketplaceDrawer } from "./roof-marketplace-drawer";
import { UploadDrawer, type UploadDrawerData } from "./upload-drawer";
import { useInspectionsRoofMarketplace } from "./use-inspections-roof-marketplace";
import { useResolvedInspectionGalleryMedia } from "./use-resolved-inspection-gallery-media";
import { WriteOutCard } from "./write-out-card";

export type StatusText = "Not started" | "Submitted" | "Resolved";
export type StatusAppearance = "warning" | "success" | "neutral";

export type GalleryData = {
  inspectionAssetIds: string[];
  proofAssetIds: string[];
  title: string;
  subHeading: string;
  statusText: StatusText;
  statusAppearance: StatusAppearance;
  // Which tab to show when the gallery opens (0 = Inspection photos, 1 = Safeguard verification):
  initialTabIndex?: 0 | 1;
  /** When set, Gallery opens with this asset selected (must be on the initial tab’s list). */
  activeAssetId?: string;
};

export type GalleryOpenPayload = {
  inspectionAssetIds: string[];
  proofAssetIds: string[];
  title: string;
  statusText: StatusText;
  statusAppearance: StatusAppearance;
  initialTabIndex?: 0 | 1;
  activeAssetId?: string;
};

const SHOW_INSPECTIONS_STATUSES = [420, 430, 440, 450, 480];

/** Satisfies Gallery’s `media.length > 0` requirement so we can use `children` for a loading spinner. */
const GALLERY_ASSET_LOADING_PLACEHOLDER: MediaTileItem[] = [
  {
    id: "__gallery-asset-loading__",
    fileName: "loading",
    name: "loading",
    description: "",
    source: "",
  },
];

const InspectionsContent = () => {
  const { inspectionsProofUpload } = useFlags();
  const isMobile = useMediaQuery({ maxWidth: screenSizes.tablet });
  const { data: policyDetail } = usePolicyDetail();
  const { agencyName } = policyDetail?.userInsuranceRep ?? {};
  const [galleryData, setGalleryData] = useState<GalleryData | null>(null);
  const [galleryTabIndex, setGalleryTabIndex] = useState<0 | 1>(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [uploadDrawerData, setUploadDrawerData] =
    useState<UploadDrawerData | null>(null);
  const navigate = useNavigate();
  const {
    error,
    inspectionDueDate,
    latestInspection,
    isLoading: loading,
    refetch,
  } = useInspectionsData();
  const {
    roofMarketplaceWorkflowEnabled,
    roofMarketplaceDataEligible,
    roofMarketplaceCanService,
    openRoofMarketplaceOrThumbtack,
    isRoofMarketplaceDrawerOpen,
    closeRoofMarketplaceDrawer,
    hasExistingBid,
    requestRoofMarketplaceBids,
  } = useInspectionsRoofMarketplace({ latestInspection });
  const referralList = latestInspection?.review?.referrals;
  const sortedReferralList = referralList?.toSorted((referral) => {
    return referral?.category === "Roof" ? -1 : 1;
  });
  const inspectionAssets = latestInspection?.review?.inspectionReviewAssets;
  const statusEnum = latestInspection?.status;
  const isPreviewWindow = latestInspection?.isPreviewWindow;
  const isIneligible = statusEnum === 480;
  const shouldRedirectToOverview =
    isPreviewWindow ||
    (statusEnum && !SHOW_INSPECTIONS_STATUSES.includes(statusEnum));
  const inspectionAssetIds = useMemo(
    () =>
      sortInspectionAssetIdsByLastUpdatedDesc(
        galleryData?.inspectionAssetIds ?? [],
        inspectionAssets,
      ),
    [galleryData?.inspectionAssetIds, inspectionAssets],
  );
  const proofAssetIds = useMemo(
    () =>
      sortInspectionAssetIdsByLastUpdatedDesc(
        galleryData?.proofAssetIds ?? [],
        inspectionAssets,
      ),
    [galleryData?.proofAssetIds, inspectionAssets],
  );
  const shownAssets =
    galleryTabIndex === 0 ? inspectionAssetIds : proofAssetIds;
  const galleryActiveIndex = useMemo(() => {
    const id = galleryData?.activeAssetId;
    if (!id || shownAssets.length === 0) {
      return 0;
    }
    const idx = shownAssets.indexOf(id);
    return idx >= 0 ? idx : 0;
  }, [galleryData?.activeAssetId, shownAssets]);
  const showGallery =
    (inspectionAssetIds.length > 0 || proofAssetIds.length > 0) &&
    galleryData != null;
  const galleryDescriptionMode =
    galleryTabIndex === 1 ? "proof-tab" : "inspection-tab";
  const { resolvedGalleryMedia, galleryMediaLoading } =
    useResolvedInspectionGalleryMedia({
      showGallery,
      shownAssetIds: shownAssets,
      inspectionAssets,
      agencyName,
      descriptionMode: galleryDescriptionMode,
    });
  const numResolved: number =
    sortedReferralList?.filter((referral) => referral?.isResolved)?.length ?? 0;
  const numTotal: number = sortedReferralList?.length ?? 0;

  useEffect(() => {
    if (showGallery) {
      hideSupportChat();
    }
    return () => showSupportChat();
  }, [showGallery]);

  useEffect(() => {
    if (shouldRedirectToOverview) navigate("/my/overview");
  }, [navigate, shouldRedirectToOverview]);

  if (error) {
    return (
      <BaseLayout>
        <MainLayout sidebar={<FaqSidebar page="overview" />}>
          <FetchingError page="Inspections" />
        </MainLayout>
      </BaseLayout>
    );
  }

  if (loading) {
    return <BaseLayout loading />;
  }

  if (isIneligible) {
    return inspectionsProofUpload ? (
      <InspectionsIneligible />
    ) : (
      <InspectionsIneligibleOld />
    );
  }

  if (showGallery) {
    const galleryAssetsLoading =
      galleryMediaLoading || resolvedGalleryMedia === null;

    return (
      <Gallery
        // Remount when loading finishes so Gallery’s thumbnail scroll effect does not use a stale active id vs new media (DS offsetLeft crash).
        key={
          galleryAssetsLoading
            ? "inspection-gallery-loading"
            : "inspection-gallery-ready"
        }
        className={`${galleryMediaTileStyles.galleryRoot} ${galleryMediaTileStyles.galleryPrimaryActiveActionButton}`}
        media={
          galleryAssetsLoading
            ? GALLERY_ASSET_LOADING_PLACEHOLDER
            : resolvedGalleryMedia
        }
        activeIndex={galleryAssetsLoading ? 0 : galleryActiveIndex}
        onClose={() => setGalleryData(null)}
        activeAction={
          <>
            <span className={styles.downloadButtonText}>Download</span>
            <Icon name="Download" />
          </>
        }
        emptyStateMessage={"When files are added, they'll appear here."}
        showHeroFooter={!galleryAssetsLoading}
        {...(galleryAssetsLoading
          ? {
              children: (
                <div className={styles.galleryLoadingBody}>
                  <Spinner />
                </div>
              ),
            }
          : {
              onActiveAction: (active: MediaTileItem) => {
                if (active.file && active.file.size > 0) {
                  downloadFile(active.file, active.fileName);
                } else if (active.source) {
                  void downloadImage(active.source, active.fileName);
                }
              },
            })}
        header={
          <Stack gap="md">
            <div className={styles.galleryTopRow}>
              <div className={styles.galleryHeader}>
                <Heading size="md">{galleryData?.title}</Heading>
                <Span color="inverse">{galleryData?.subHeading}</Span>
              </div>
              <div className={styles.statusAndCloseButton}>
                {!isMobile ? (
                  <Span size="sm" color="inverse" className={styles.status}>
                    <Badge.Indicator
                      size="medium"
                      appearance={galleryData.statusAppearance}
                    />
                    {galleryData.statusText}
                  </Span>
                ) : null}
                <Button
                  appearance="neutral"
                  className={styles.closeButton}
                  icon="Close"
                  onPress={() => setGalleryData(null)}
                  size="sm"
                  data-testid="inspection-gallery-close-btn"
                />
              </div>
            </div>
            <div className={styles.galleryTabGroup}>
              <TabGroup
                activeTabIndex={galleryTabIndex}
                variant="inverse"
                border={true}
                tabs={[
                  {
                    className: styles.galleryTabItem,
                    name: "Inspection photos",
                    count: inspectionAssetIds.length,
                    onClick: () => setGalleryTabIndex(0),
                  },
                  {
                    className: styles.galleryTabItem,
                    name: "Safeguard verification",
                    count: proofAssetIds.length,
                    onClick: () => setGalleryTabIndex(1),
                  },
                ]}
              />
            </div>
          </Stack>
        }
      />
    );
  }

  return (
    <>
      <BaseLayout>
        <MainLayout
          topRow={
            numResolved < numTotal ? (
              <div className={styles.topRow}>
                <Notification
                  appearance="warning"
                  description={
                    <>
                      <Span bold>
                        {numResolved} out of {numTotal} required safeguards
                        resolved.{" "}
                      </Span>
                      Please resolve the required safeguards{" "}
                      {inspectionDueDate ? (
                        <>
                          by <Span bold>{formatDate(inspectionDueDate)} </Span>
                        </>
                      ) : null}
                      and upload files and photos here or send them to your
                      insurance representative.
                    </>
                  }
                  title=""
                />
              </div>
            ) : null
          }
          sidebar={<InsuranceRepresentativeNew />}
        >
          {sortedReferralList?.length ? (
            <Stack gap="xl">
              {sortedReferralList.map((referral, idx) => {
                const gallerySubHeading =
                  formatReferralWriteOutCardSubheading(referral);
                return (
                  <WriteOutCard
                    referral={referral ?? {}}
                    showUploadDrawer={(data) => {
                      setUploadDrawerData(data);
                      setDrawerOpen(true);
                    }}
                    showGallery={(payload) => {
                      const hasPhotos =
                        (payload.inspectionAssetIds?.length ?? 0) > 0 ||
                        (payload.proofAssetIds?.length ?? 0) > 0;
                      if (hasPhotos) {
                        setGalleryData({
                          inspectionAssetIds: payload.inspectionAssetIds ?? [],
                          proofAssetIds: payload.proofAssetIds ?? [],
                          title: payload.title,
                          subHeading: gallerySubHeading,
                          statusText: payload.statusText,
                          statusAppearance: payload.statusAppearance,
                          initialTabIndex: payload.initialTabIndex ?? 0,
                          activeAssetId: payload.activeAssetId,
                        });
                        setGalleryTabIndex(payload.initialTabIndex ?? 0);
                      }
                    }}
                    roofMarketplaceEligible={roofMarketplaceDataEligible}
                    roofMarketplaceDrawerAvailable={
                      roofMarketplaceCanService === true
                    }
                    onOpenRoofMarketplace={
                      roofMarketplaceWorkflowEnabled
                        ? openRoofMarketplaceOrThumbtack
                        : undefined
                    }
                    key={idx}
                  />
                );
              })}
            </Stack>
          ) : null}

          <UploadDrawer
            drawerOpen={drawerOpen}
            onClose={() => {
              setDrawerOpen(false);
              setUploadDrawerData(null);
            }}
            onUploadSuccess={refetch}
            uploadData={uploadDrawerData}
          />
        </MainLayout>
      </BaseLayout>
      {roofMarketplaceWorkflowEnabled ? (
        <RoofMarketplaceDrawer
          isOpen={isRoofMarketplaceDrawerOpen}
          onClose={closeRoofMarketplaceDrawer}
          hasExistingBid={hasExistingBid}
          onRequestBids={requestRoofMarketplaceBids}
        />
      ) : null}
    </>
  );
};

/**
 * `InspectionsContent` reads `ThumbtackModalContext` (via `useInspectionsRoofMarketplace`),
 * so the provider has to live in an ancestor — a component cannot consume context from a
 * provider it renders itself. See PPIX-2726.
 */
export const Inspections = () => (
  <ThumbtackModalProvider>
    <InspectionsContent />
  </ThumbtackModalProvider>
);
